// Foreground colour for text sitting on a slot-palette background.
//
// Kept in its own module (rather than inside printAgent) so the scan-path
// components can import it without pulling in jsPDF/JsBarcode and the whole
// print-agent client. printAgent re-exports it, so both names work.

// Perceptual lightness below which a background wants white text. CIELAB L* is
// a uniform lightness scale — 50 is the visual midpoint — and 60 is the
// long-standing convention for this decision.
//
// NOT the WCAG 2.x contrast ratio. That formula is skewed towards black on
// mid-tones: it scores black over white on mid grey (#808080) and mid brown
// (#A67C52), which is visibly the wrong call and is exactly the defect APCA was
// created to correct. L* agrees with APCA on 9 of these 11 colours, and on the
// two it doesn't (green #3CB44B, orange #F58231) APCA is within 3 Lc — a
// coin-flip — while black is the better-established pairing on both.
//
// For this palette the threshold falls in clean air: the nearest colours either
// side are magenta at L* 58.5 and green at L* 65.1, so no slot sits on the
// boundary where a small colour tweak would flip its text.
const WHITE_TEXT_BELOW_LSTAR = 60;

/** Whichever of black or white reads better on `hex`.
 *  White for the darker half — blue, purple, grey, brown, magenta, black;
 *  black for the lighter half — green, yellow, orange, lime, cyan. */
export function readableTextOn(hex: string | null | undefined): string {
    const h = String(hex ?? "").replace("#", "");
    const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    if (full.length < 6) return "#000000";
    const channel = (i: number) => {
        const v = parseInt(full.slice(i, i + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const y = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    const lStar = y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y;
    return lStar < WHITE_TEXT_BELOW_LSTAR ? "#FFFFFF" : "#000000";
}
